import "./Transporters.scss";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TransporterService from "../../services/TransporterService";
import { useLoader } from "../../contexts/LoaderContext";
import SearchBar from "../../components/SearchBar/SearchBar";
import TableComponent from "../../components/TableComponent/TableComponent";
import StatsBoxContainer from "../../components/StatsBoxContainer/StatsBoxContainer";
import { useLoading } from "../../contexts/LoadingContext";
import { showToast } from "../../components/Toaster";
import { SelectChangeEvent } from "@mui/material";

interface Transporter {
  id: number;
  name: string;
}

interface TransporterStats {
  totalTransporters: number;
}

function Transporters() {
  const { showLoader, hideLoader } = useLoader();
  const loader = useLoading();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [transporters, setTransporters] = useState<Transporter[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const autoRefreshInterval = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshTime = useRef<number>(0);
  const REFRESH_COOLDOWN = 2000; // 2 seconds cooldown between refreshes
  const [transportersLoaded, setTransportersLoaded] = useState(false);
  const initialLoadDone = useRef(false);
  const isLoading = useRef(false);
  const [transporterStats, setTransporterStats] = useState<TransporterStats>({
    totalTransporters: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchTransporters = useCallback(
    async (suppressLoader?: boolean, forceRefresh?: boolean) => {
      const now = Date.now();
      if (!forceRefresh && now - lastRefreshTime.current < REFRESH_COOLDOWN) {
        console.log("Refresh skipped - too soon since last refresh");
        if (!suppressLoader) {
          hideLoader();
        } else {
          loader.setLoading(false);
        }
        return;
      }
      lastRefreshTime.current = now;

      try {
        if (!initialLoadDone.current) {
          setTransportersLoaded(false);
        }

        if (!suppressLoader) {
          showLoader();
        } else {
          loader.setLoading(true);
        }

        setError(null);

        const response = await TransporterService.getMyTransporters();

        if (!response.data.error) {
          const transporters: Transporter[] = response.data.data;
          setTransporters(transporters);

          // Calculate stats
          const stats: TransporterStats = {
            totalTransporters: transporters.length,
          };
          setTransporterStats(stats);
        } else {
          console.error(
            "Error in fetching transporters:",
            response.data.message
          );
          setError(response.data.message || "Error fetching transporters");
        }
      } catch (err: any) {
        console.error("Error fetching data:", err);
        setError(err.message || "An error occurred while fetching data");
      } finally {
        setTransportersLoaded(true);
        initialLoadDone.current = true;

        setTimeout(() => {
          if (!suppressLoader) {
            hideLoader();
          } else {
            loader.setLoading(false);
          }
        }, 1000);
      }
    },
    [showLoader, hideLoader, loader]
  );

  // Memoize table data transformation
  const tableData = useMemo(() => {
    return transporters.map((transporter) => ({
      id: transporter.id,
      name: transporter.name,
    }));
  }, [transporters]);

  // Memoize filtered and paginated data
  const filteredAndPaginatedData = useMemo(() => {
    let filteredData = tableData;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter((row) =>
        row.name.toLowerCase().includes(query)
      );
    }

    // Calculate pagination
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      data: filteredData.slice(startIndex, endIndex),
      totalElements: filteredData.length,
      totalPages: Math.ceil(filteredData.length / pageSize),
    };
  }, [tableData, searchQuery, currentPage, pageSize]);

  // Memoize search suggestions
  const searchSuggestionsMemo = useMemo(() => {
    if (tableData.length > 0) {
      const suggestions = new Set<string>();

      tableData.forEach((row) => {
        if (row.name) suggestions.add(row.name);
      });

      return Array.from(suggestions);
    }
    return [];
  }, [tableData]);

  // Memoize columns definition
  const columns = useMemo(
    () => [
      // {
      //   header: "Transporter ID",
      //   accessor: "id",
      //   sortable: true,
      //   centerAlign: true,
      //   width: "100px",
      // },
      {
        header: "Transporter Name",
        accessor: "name",
        sortable: true,
      },
    ],
    []
  );

  // Memoize stats data
  const statsData = useMemo(
    () => [
      {
        name: "Total Transporters",
        value: transporterStats.totalTransporters,
        valueColor: "#59585B",
      },
    ],
    [transporterStats]
  );

  // Initial data fetch and auto-refresh setup
  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchTransporters(false);
      } finally {
        // No need to hide loader here as it's handled in fetchTransporters
      }
    };

    fetchData();

    if (autoRefreshEnabled) {
      autoRefreshInterval.current = setInterval(() => {
        fetchTransporters(true);
      }, 30000); // Refresh every 30 seconds
    }

    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }
    };
  }, [fetchTransporters, autoRefreshEnabled]);

  // Update search suggestions when table data changes
  useEffect(() => {
    setSearchSuggestions(searchSuggestionsMemo);
  }, [searchSuggestionsMemo]);

  // Toggle auto-refresh
  const toggleAutoRefresh = useCallback(() => {
    setAutoRefreshEnabled((prev) => !prev);
  }, []);

  // Handle manual refresh
  const handleManualRefresh = useCallback(() => {
    fetchTransporters(true, true);
  }, [fetchTransporters]);

  // Handle search input changes
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      setPageSize(Number(event.target.value));
      setCurrentPage(1); // Reset to first page when changing page size
    },
    []
  );

  return (
    <div className="transporters-page p-[20px]">
      <StatsBoxContainer stats={statsData} />

      <div className="flex justify-between items-center py-[30px]">
        <SearchBar
          placeholder="Search"
          onSearch={handleSearchChange}
          showClearButton={true}
          suggestions={searchSuggestions}
          onSuggestionClick={(suggestion) => {
            setSearchQuery(suggestion);
            setCurrentPage(1); // Reset to first page on suggestion click
          }}
        />
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              className={`secondary_button ${
                loader.isLoading ? "opacity-70" : ""
              }`}
              onClick={handleManualRefresh}
              disabled={loader.isLoading}
            >
              <div className="flex items-center">
                {loader.isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Refreshing...
                  </>
                ) : (
                  "Refresh"
                )}
              </div>
            </button>
            <button
              type="button"
              className={`secondary_button ${
                autoRefreshEnabled ? "active" : ""
              }`}
              onClick={toggleAutoRefresh}
              title={
                autoRefreshEnabled
                  ? "Auto-refresh enabled (every 30s)"
                  : "Auto-refresh disabled"
              }
            >
              <div className="flex items-center">
                {autoRefreshEnabled ? "Auto-refresh On" : "Auto-refresh Off"}
              </div>
            </button>
          </div>
        </div>
      </div>

      {transportersLoaded && !loader.isLoading && tableData.length === 0 ? (
        <div className="flex justify-center items-center">
          <p className="text-gray-500">No transporters found</p>
        </div>
      ) : (
        <div className="table">
          <TableComponent
            showPagination={true}
            columns={columns}
            data={tableData}
            searchQuery={searchQuery}
            showCheckboxes={false}
            uniqueAccessor="id"
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={Math.ceil(tableData.length / pageSize)}
            totalElements={tableData.length}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            enableClientSideFiltering={true}
          />
        </div>
      )}
    </div>
  );
}

export default Transporters;
